import AtlassianSwitcher from "@atlassian/switcher";
import { createJoinableSitesProvider } from "@atlassian/switcher/create-custom-provider";

import styled from "styled-components";
import { B400 } from "@atlaskit/theme/colors";
import React, { useState, useEffect, useMemo } from "react";
// @ts-ignore
import { IntlProvider } from "react-intl";
import Environment from "../../common/environment";
import AppSwitcherIcon from "@atlaskit/icon/glyph/app-switcher";

import { hamletUser } from "../../common/hamlet/hamlet-user";
import { JoinableSite } from "@atlassian/switcher/dist/types/types";
import storage from "../../utils/storage";
import DiscoverSection from "./DiscoverSection";
import { useFeatureFlags } from "@atlassiansox/bxp-react-feature-flags";
import tracking from "../../utils/tracking";

import RemoteSwitcherPlugin from "@atlassian/remote-module-loader/plugin-switcher";
import { RemoteComponent } from "@atlassian/remote-module-loader/remote-component";
import useRemoteModule from "@atlassian/remote-module-loader/use-remote-module";

type JoinableSitesResponseJson = {
  joinableSites?: Array<JoinableSite>;
};

type SwitcherComponentProps = React.ComponentProps<typeof AtlassianSwitcher>;

const AVATAR_IMG_URL =
  "http://wac-cdn.atlassian.com/dam/wac/icons/ADG3-Icons-36px-N700/object-avatar-man.svg";

const remoteSwitcherPlugin = new RemoteSwitcherPlugin({
  env:
    Environment.getEnvironment() === Environment.production()
      ? "production"
      : "staging",
  variant: "latest",
});

const RemoteSwitcherPreloader = () => {
  useRemoteModule(remoteSwitcherPlugin);

  return null;
};

const WacAppSwitcher = (props: {
  allowWacJoinSitesProvider: boolean;
  wacAppSwitcherLabel: string;
  domRootId?: string;
}) => {
  const [isAppSwitcherMenu, setAppSwitcherMenu] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [joinableSites, setJoinableSites] = useState(null);
  const [isFirstSwitcherOpen, setIsFirstSwitcherOpen] = useState(true);
  // only load <AtlassianSwitcher/> on 1st hover
  const [hasHovered, setHasHovered] = useState(false);

  const {
    "expcen-1501_variation_1": expcen1501Variation1,
    "expcen-1501_variation_2": expcen1501Variation2,
  } = useFeatureFlags();
  const [switcherAtlas] = useState(expcen1501Variation1);
  const [switcherJwm] = useState(expcen1501Variation2);

  const switcherProps = useMemo<SwitcherComponentProps>(
    () => ({
      product: "WAC",
      appearance: "standalone",
      joinableSitesDataProvider: joinableSites,
      onJoinableSiteClicked: () => {
        setAppSwitcherMenu(false);

        // joinable sites might now change if user follows through joining this site
        // so dump joinable sites data to indicate it must be reloaded
        setJoinableSites(null);
      },
    }),
    [joinableSites]
  );

  async function getUserData() {
    const userInfo = await getUserInfo();
    setIsLoggedIn(userInfo.isLoggedIn() || false);
  }

  async function getJoinableSites() {
    const sites: JoinableSitesResponseJson = await fetch(
      `${Environment.getAPIPrivateURL()}/joinable-sites`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          products: [
            "confluence.ondemand",
            "jira-software.ondemand",
            "jira-servicedesk.ondemand",
            "jira-core.ondemand",
          ],
        }),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      }
    ).then((res) => res.json());

    const reMappedSitesData = (sites.joinableSites || []).map((site) => {
      const userData: any = {};
      Array.prototype.forEach.call(site.products, (val: string | number) => {
        userData[val] = [{ avatarUrl: AVATAR_IMG_URL, displayName: "" }];
      });
      return {
        cloudId: site.cloudId ? site.cloudId : "",
        displayName: site.displayName ? site.displayName : "",
        url: site.url ? site.url : "",
        avatarUrl: site.avatarUrl ? site.avatarUrl : undefined,
        relevance: site.relevance ? site.relevance : undefined,
        users: userData,
      };
    });

    const joinableSitesPromiseMaker = () => {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ sites: reMappedSitesData });
        });
      });
    };

    // TODO: Fix types
    setJoinableSites(
      createJoinableSitesProvider(joinableSitesPromiseMaker as any) as any
    );
  }

  async function toggleSwitcherDropdown() {
    if (isFirstSwitcherOpen) {
      setIsFirstSwitcherOpen(false);
    } else if (joinableSites === null) {
      // reload the joinable sites data if a joinable site has been clicked
      if (props.allowWacJoinSitesProvider) {
        getJoinableSites();
      }
    }
    if (isAppSwitcherMenu) {
      // user closing switcher
      tracking.trackEvent("WacAppSwitcherIconClickedToClose");
    } else {
      // user opening switcher
      tracking.trackEvent("WacAppSwitcherIconClickedToOpen");
    }
    setAppSwitcherMenu(!isAppSwitcherMenu);
  }

  document.addEventListener("click", (event: any) => {
    const path = event.path || (event.composedPath && event.composedPath());
    const insideAppswitcher = Array.prototype.includes.call(
      Array.prototype.map.call(path, (el: { id: string }) => {
        return el.id === (props.domRootId && props.domRootId.length > 0 ? props.domRootId : "global-nav--wac__app-switcher");
      }),
      true
    );

    if (!insideAppswitcher) {
      setAppSwitcherMenu(false);
    }
  });

  document.addEventListener("keydown", (event: any) => {
    if (event.code == "Escape" && isAppSwitcherMenu) {
      setAppSwitcherMenu(false);
    }
  });

  useEffect(() => {
    if (storage.getCookie("login_user_detected") === "true") {
      if (props.allowWacJoinSitesProvider) {
        getJoinableSites();
      }
      getUserData();
    }
  }, []);

  useEffect(() => {
    if (isAppSwitcherMenu) {
      if (typeof ace !== "undefined") {
        ace.analytics.Tracking.pageView(
          "Wac App Switcher",
          "WacAppSwitcherMenuRendered"
        );
      }
    }
  }, [isAppSwitcherMenu]);

  const appSwitcher = (
    <AppSwitcherMenu>
      <RemoteComponent<SwitcherComponentProps>
        remotePlugin={remoteSwitcherPlugin}
        analyticsChannel="navigation"
        ErrorFallback={<AtlassianSwitcher {...switcherProps} />}
        {...switcherProps}
      />
      {(switcherAtlas || switcherJwm) && (
        <DiscoverSection variation={switcherAtlas || switcherJwm} />
      )}
    </AppSwitcherMenu>
  );

  if (!isLoggedIn) {
    return null;
  }

  return (
    <IntlProvider locale={document.documentElement.lang}>
      <AppSwitcherContainer
        className={props.wacAppSwitcherLabel.length ? "has-text-label" : ""}
      >
        <StyledAppSwitcherIcon
          tabIndex={0}
          className={
            props.wacAppSwitcherLabel.length ? "text-label-parent" : ""
          }
          data-label={props.wacAppSwitcherLabel}
          onClick={() => {
            toggleSwitcherDropdown();
          }}
          onKeyDown={(e) => {
            if (e.code == "Enter") {
              toggleSwitcherDropdown();
            }
          }}
          onFocus={() => setHasHovered(true)}
          onMouseEnter={() => setHasHovered(true)}
        >
          <AppSwitcherIcon
            size={"large"}
            primaryColor="#0747a6"
            label="App Switcher"
          />
        </StyledAppSwitcherIcon>
        <Container displayVal={isAppSwitcherMenu} className={isAppSwitcherMenu ? 'app-switcher-menu-open' : 'app-switcher-menu-close'}>
          {hasHovered && <RemoteSwitcherPreloader />}
          {hasHovered && appSwitcher}
        </Container>
      </AppSwitcherContainer>
    </IntlProvider>
  );
};

const getUserInfo = () => {
  const PERFORMANCE_BOOST_COOKIE = "WCF-512";
  const performanceBoost = storage.getCookie(PERFORMANCE_BOOST_COOKIE) !== null;
  return hamletUser.getStargateProfile(performanceBoost).then(() => {
    return hamletUser;
  });
};

const Container = styled.div`
  display: ${(props: { displayVal: boolean }) =>
    props.displayVal ? "block" : "none"};
  position: relative;
  right: 330px;
  top: 36px;
`;

const AppSwitcherContainer = styled.div`
  display: flex;
  justify-content: center;

  :hover {
    background: #ebecf0;
    border-radius: 25px;
  }
  ${Container}:hover {
    display: block;
  }

  &.has-text-label {
    border-radius: 5px;
  }
`;

const StyledAppSwitcherIcon = styled.div`
  span {
    height: 28px;
    width: 28px;
  }
  display: flex;
  justify-content: center;
  cursor: pointer;

  &.text-label-parent {
    display: flex;
    align-items: center;
    border: 1px solid ${B400};
    padding: 0 10px 0 14px;
    border-radius: 5px;

    &:before {
      content: attr(data-label);
      margin: 0 5px 0 0;
      color: ${B400};
      font-size: 0.875rem;
      line-height: 2.15;
    }

    & > span {
      width: 22px;
      height: 22px;
    }
  }
`;

const AppSwitcherMenu = styled.div`
  display: block;
  position: absolute;
  border-radius: 5px;
  background-color: white;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  padding: 0;
  z-index: 19;
  svg {
    margin-bottom: 0;
  }
  h1 {
    margin-top: 0px;
    font-weight: normal;
    font-weight: 500;
  }
  a {
    font-weight: 400;
  }
`;

export default WacAppSwitcher;
